import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  DollarSign, 
  Users, 
  TrendingUp,
  Percent,
  Settings,
  Download,
  ChevronRight,
  Building2,
  Calendar,
  PiggyBank,
  Wallet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEarnings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDetails, setWorkerDetails] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [commissionRate, setCommissionRate] = useState(2);
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    fetchEarnings();
  }, [selectedMonth, selectedYear]);

  const fetchEarnings = async () => {
    setLoading(true);
    try {
      let url = `${API}/earnings/overview`;
      const params = [];
      if (selectedMonth) params.push(`month=${selectedMonth}`);
      if (selectedYear) params.push(`year=${selectedYear}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setEarningsData(response.data);
      setCommissionRate(response.data.commission_rate);
    } catch (error) {
      console.error("Error fetching earnings:", error);
      toast.error("Failed to load earnings data");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkerDetails = async (userId) => {
    try {
      let url = `${API}/earnings/worker/${userId}`;
      const params = [];
      if (selectedMonth) params.push(`month=${selectedMonth}`);
      if (selectedYear) params.push(`year=${selectedYear}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setWorkerDetails(response.data);
      setDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load worker details");
    }
  };

  const handleUpdateCommission = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform/settings?commission_rate=${commissionRate}`);
      toast.success("Commission rate updated!");
      setSettingsOpen(false);
      fetchEarnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const months = [
    { value: "", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) {
    years.push(y.toString());
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Earnings & Payroll">
      <div className="space-y-6" data-testid="admin-earnings-page">
        {/* Header with filters */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-40 bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-white hover:bg-white/10">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32 bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {years.map(y => (
                  <SelectItem key={y} value={y} className="text-white hover:bg-white/10">
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            onClick={() => setSettingsOpen(true)}
            variant="outline"
            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
          >
            <Settings className="w-4 h-4 mr-2" />
            Commission Settings
          </Button>
        </div>

        {/* Summary Cards */}
        {earningsData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400">
                      {formatCurrency(earningsData.totals.gross_earnings)}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Gross Earnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <PiggyBank className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-400">
                      {formatCurrency(earningsData.totals.platform_commission)}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Platform Commission ({earningsData.commission_rate}%)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                    <Wallet className="w-5 h-5 text-[#23F7DD]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#23F7DD]">
                      {formatCurrency(earningsData.totals.net_payable)}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Net Payable</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-400">
                      {earningsData.totals.workers_count}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Active Workers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/10 rounded-lg">
                    <Building2 className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-400">
                      {earningsData.totals.agreements_count}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Completed Projects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Workers Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#23F7DD]" />
              Worker Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : !earningsData?.workers?.length ? (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No earnings data for this period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#94A3B8]">Worker</TableHead>
                      <TableHead className="text-[#94A3B8]">Code</TableHead>
                      <TableHead className="text-[#94A3B8]">Role</TableHead>
                      <TableHead className="text-[#94A3B8] text-right">Projects</TableHead>
                      <TableHead className="text-[#94A3B8] text-right">Gross</TableHead>
                      <TableHead className="text-[#94A3B8] text-right">Commission</TableHead>
                      <TableHead className="text-[#94A3B8] text-right">Net Payable</TableHead>
                      <TableHead className="text-[#94A3B8]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {earningsData.workers.map((worker) => (
                      <TableRow 
                        key={worker.user_id} 
                        className="border-white/10 hover:bg-white/5 cursor-pointer"
                        onClick={() => fetchWorkerDetails(worker.user_id)}
                      >
                        <TableCell className="text-white font-medium">
                          {worker.user_name}
                        </TableCell>
                        <TableCell className="text-[#94A3B8]">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            worker.worker_code?.startsWith('TL-') 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-[#23F7DD]/20 text-[#23F7DD]'
                          }`}>
                            {worker.worker_code}
                          </span>
                        </TableCell>
                        <TableCell className="text-[#94A3B8] capitalize">
                          {worker.role?.replace('_', ' ')}
                        </TableCell>
                        <TableCell className="text-white text-right">
                          {worker.agreements_count}
                        </TableCell>
                        <TableCell className="text-green-400 text-right font-medium">
                          {formatCurrency(worker.gross_earnings)}
                        </TableCell>
                        <TableCell className="text-purple-400 text-right">
                          {formatCurrency(worker.platform_commission)}
                        </TableCell>
                        <TableCell className="text-[#23F7DD] text-right font-bold">
                          {formatCurrency(worker.net_earnings)}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-[#475569]" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Worker Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded']">Worker Earnings Details</DialogTitle>
            </DialogHeader>
            
            {workerDetails && (
              <div className="space-y-6">
                {/* Worker Info */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">{workerDetails.worker.name}</h3>
                      <p className="text-[#94A3B8]">{workerDetails.worker.email}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      workerDetails.worker.worker_code?.startsWith('TL-') 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-[#23F7DD]/20 text-[#23F7DD]'
                    }`}>
                      {workerDetails.worker.worker_code}
                    </span>
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-500/10 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-400">
                      {formatCurrency(workerDetails.summary.gross_earnings)}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Gross</p>
                  </div>
                  <div className="p-4 bg-purple-500/10 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-400">
                      {formatCurrency(workerDetails.summary.platform_commission)}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Commission ({workerDetails.commission_rate}%)</p>
                  </div>
                  <div className="p-4 bg-[#23F7DD]/10 rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#23F7DD]">
                      {formatCurrency(workerDetails.summary.net_earnings)}
                    </p>
                    <p className="text-xs text-[#94A3B8]">Net Payable</p>
                  </div>
                </div>

                {/* Projects List */}
                <div>
                  <h4 className="text-white font-medium mb-3">Projects ({workerDetails.summary.projects_count})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {workerDetails.projects.map((project, idx) => (
                      <div key={idx} className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="text-white">{project.project_title}</p>
                          <p className="text-xs text-[#94A3B8]">
                            {project.percentage}% share • {new Date(project.date).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-[#23F7DD] font-medium">{formatCurrency(project.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Commission Settings Dialog */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
                <Percent className="w-5 h-5 text-purple-400" />
                Platform Commission
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              <p className="text-[#94A3B8] text-sm">
                Set the platform commission rate. This percentage will be deducted from each worker's earnings.
              </p>
              
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Commission Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(parseFloat(e.target.value))}
                  className="bg-black/50 border-white/10 text-white text-2xl text-center focus:border-purple-500"
                />
                <p className="text-xs text-[#94A3B8] text-center">
                  Current: {earningsData?.commission_rate || 2}%
                </p>
              </div>
              
              <div className="p-4 bg-purple-500/10 rounded-lg">
                <p className="text-sm text-[#94A3B8]">
                    Example: If a worker earns <span className="text-white">10,000 NOK</span>, 
                    platform commission will be <span className="text-purple-400">{formatCurrency(10000 * commissionRate / 100)}</span>,
                    and worker receives <span className="text-[#23F7DD]">{formatCurrency(10000 * (100 - commissionRate) / 100)}</span>.
                </p>
              </div>
              
              <Button
                onClick={handleUpdateCommission}
                disabled={saving}
                className="w-full bg-purple-500 text-white hover:bg-purple-600"
              >
                {saving ? "Saving..." : "Update Commission Rate"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
